import type {
  CatalogMerchant,
  CatalogProduct,
  Department,
  DepartmentId,
  RecentInterestInput,
  SearchCriteria,
} from "./types";

export const DEPARTMENTS: readonly Department[] = [
  { id: "electronics", label: "Electronics", cue: "Audio, computing, smart home", query: "everyday electronics" },
  { id: "home_garden", label: "Home & garden", cue: "Furniture, lighting, outdoor", query: "home and garden" },
  { id: "kitchen_dining", label: "Kitchen & dining", cue: "Coffee, cookware, table", query: "kitchen and dining" },
  { id: "sports_outdoors", label: "Sports & outdoors", cue: "Running, training, camping", query: "sports and outdoors" },
  { id: "fashion", label: "Fashion", cue: "Everyday clothing and accessories", query: "fashion essentials" },
  { id: "beauty_personal_care", label: "Beauty & care", cue: "Skin, hair, personal care", query: "beauty and personal care" },
  { id: "travel_luggage", label: "Travel & luggage", cue: "Bags, organizers, adapters", query: "travel luggage" },
  { id: "office", label: "Office", cue: "Desks, seating, productivity", query: "home office" },
  { id: "toys_hobbies", label: "Toys & hobbies", cue: "Games, building, creative play", query: "toys and hobbies" },
  { id: "pet_supplies", label: "Pet supplies", cue: "Food, comfort, walking", query: "pet supplies" },
] as const;

export const SAFE_HANDOFF_INTERESTS: readonly RecentInterestInput[] = [
  {
    label: "A calmer home office",
    query: "ergonomic home office essentials",
    department: "office",
    strength: "active",
    recency: "this_week",
    budget: { currency: "EUR", max: 45000 },
    reason: "Deterministic non-sensitive handoff example.",
    scope: "session",
  },
  {
    label: "Better morning coffee",
    query: "coffee brewing essentials",
    department: "kitchen_dining",
    strength: "exploring",
    recency: "this_month",
    reason: "Deterministic non-sensitive handoff example.",
    scope: "session",
  },
  {
    label: "Everyday running",
    query: "daily running essentials",
    department: "sports_outdoors",
    strength: "active",
    recency: "today",
    reason: "Deterministic non-sensitive handoff example.",
    scope: "session",
  },
  {
    label: "Travel lighter",
    query: "carry on travel organizers",
    department: "travel_luggage",
    strength: "exploring",
    recency: "this_month",
    reason: "Deterministic non-sensitive handoff example.",
    scope: "session",
  },
] as const;

const merchant = (slug: string, name: string): CatalogMerchant => ({
  id: `gid://shopify/Shop/fixture-${slug}`,
  name,
  url: `https://example.com/${slug}`,
  domain: "example.com",
  policyLinks: [
    { type: "shipping_policy", url: `https://example.com/${slug}/shipping` },
    { type: "refund_policy", url: `https://example.com/${slug}/returns` },
  ],
});

interface FixtureSeed {
  key: string;
  department: DepartmentId;
  title: string;
  description: string;
  price: number;
  currency?: string;
  seller: string;
  image: string;
  facts: readonly string[];
  available?: boolean;
}

const seeds: readonly FixtureSeed[] = [
  { key: "audio", department: "electronics", title: "Quietline wireless headphones", description: "Over-ear listening with adjustable active noise control and a folding case.", price: 14900, seller: "Northline Audio", image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=900&q=80", facts: ["Bluetooth 5.3", "42-hour battery", "USB-C"] },
  { key: "lamp", department: "home_garden", title: "Arc task floor lamp", description: "An adjustable floor light for focused work and warm ambient light.", price: 12900, seller: "Common Ground Home", image: "https://images.unsplash.com/photo-1507473885765-e6ed057f782c?auto=format&fit=crop&w=900&q=80", facts: ["Dimmable", "Warm white", "Steel base"] },
  { key: "coffee", department: "kitchen_dining", title: "Compact precision brewer", description: "A small programmable coffee brewer with a thermal carafe and reusable filter.", price: 9900, seller: "Daily Table", image: "https://images.unsplash.com/photo-1517668808822-9ebb02f2a0e6?auto=format&fit=crop&w=900&q=80", facts: ["1.1 litre", "Thermal carafe", "Reusable filter"] },
  { key: "shoes", department: "sports_outdoors", title: "Tempo daily running shoes", description: "Neutral road shoes with a breathable upper and moderate cushioning.", price: 11800, seller: "Field & Pace", image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80", facts: ["Neutral support", "8 mm drop", "Road"] },
  { key: "jacket", department: "fashion", title: "Everyday rain shell", description: "A light packable shell for changeable weather and daily travel.", price: 8900, seller: "Everyday Standard", image: "https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?auto=format&fit=crop&w=900&q=80", facts: ["Water resistant", "Packable", "Recycled nylon"] },
  { key: "care", department: "beauty_personal_care", title: "Simple daily care set", description: "A fragrance-free cleanser and moisturizer pair for a short routine.", price: 3200, seller: "Plain Form", image: "https://images.unsplash.com/photo-1556229010-6c3f2c9ca5f8?auto=format&fit=crop&w=900&q=80", facts: ["Fragrance free", "Two steps", "Recyclable bottles"] },
  { key: "carry", department: "travel_luggage", title: "Transit carry-on backpack", description: "A cabin-sized clamshell backpack with a separate laptop sleeve.", price: 13500, seller: "Open Route", image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=900&q=80", facts: ["35 litre", "16-inch laptop", "Clamshell"] },
  { key: "chair", department: "office", title: "Adjustable work chair", description: "A breathable work chair with adjustable seat depth and lumbar support.", price: 29900, seller: "Working Room", image: "https://images.unsplash.com/photo-1580480055273-228ff5388ef8?auto=format&fit=crop&w=900&q=80", facts: ["Adjustable lumbar", "Seat depth", "Five-year warranty"] },
  { key: "blocks", department: "toys_hobbies", title: "City builder block set", description: "An open-ended building set with reusable storage and illustrated prompts.", price: 5400, seller: "Play Common", image: "https://images.unsplash.com/photo-1598880940080-ff9a29891b85?auto=format&fit=crop&w=900&q=80", facts: ["650 pieces", "Ages 7+", "Storage tray"] },
  { key: "petbed", department: "pet_supplies", title: "Washable pet rest mat", description: "A low-profile padded mat with a removable machine-washable cover.", price: 4700, seller: "Good Companion", image: "https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&w=900&q=80", facts: ["Washable cover", "Non-slip base", "Medium"] },
  { key: "keyboard", department: "office", title: "Low-profile wireless keyboard", description: "A compact multi-device keyboard for quieter shared workspaces.", price: 7900, currency: "EUR", seller: "Northline Audio", image: "https://images.unsplash.com/photo-1587829741301-dc798b83add3?auto=format&fit=crop&w=900&q=80", facts: ["Multi-device", "Rechargeable", "Quiet keys"] },
  { key: "organizers", department: "travel_luggage", title: "Four-piece packing organizer set", description: "Breathable travel organizers in four useful sizes.", price: 3900, currency: "GBP", seller: "Open Route", image: "https://images.unsplash.com/photo-1553531384-cc64ac80f931?auto=format&fit=crop&w=900&q=80", facts: ["Four sizes", "Washable", "Mesh panel"] },
];

const makeProduct = (seed: FixtureSeed, index: number): CatalogProduct => {
  const observedAt = "2026-08-28T00:00:00.000Z";
  const productId = `gid://shopify/p/fixture-${seed.key}`;
  const merchantInfo = merchant(seed.key, seed.seller);
  const currency = seed.currency ?? "EUR";
  const evidence = [
    { id: `${seed.key}-title`, field: "title", kind: "merchant_fact" as const, value: seed.title, reason: "Supplied by the fixture merchant record.", observedAt },
    { id: `${seed.key}-price`, field: "price", kind: "merchant_fact" as const, value: `${seed.price} ${currency} minor units`, reason: "Supplied by the fixture merchant record.", observedAt },
    { id: `${seed.key}-description`, field: "description", kind: "catalog_inference" as const, value: seed.description, reason: "Fixture catalog enrichment; not merchant verification.", observedAt },
    { id: `${seed.key}-check`, field: "availability", kind: "productist_check" as const, value: seed.available === false ? "Unavailable" : "Available", reason: "Productist checked the structured availability field.", observedAt },
    { id: `${seed.key}-unknown`, field: "delivery", kind: "unknown" as const, reason: "A delivery estimate is not present in this fixture.", observedAt },
  ];
  const baseVariant: CatalogProduct["variants"][number] = {
    id: `gid://shopify/ProductVariant/fixture-${seed.key}`,
    productId,
    title: `${seed.title} — Standard`,
    description: seed.description,
    url: `https://example.com/${seed.key}/product`,
    price: { amount: seed.price, currency },
    available: seed.available ?? true,
    media: [{ type: "image", url: seed.image, altText: seed.title, evidence: "merchant_fact" }],
    merchant: merchantInfo,
    checkoutUrl: `https://example.com/${seed.key}/checkout`,
    rating: { value: 4.1 + ((index % 7) / 10), scaleMin: 1, scaleMax: 5, count: 18 + index * 17 },
    condition: ["new"],
    nativeCheckoutEligible: false,
    selectedOptions: [{ name: "Finish", label: "Standard" }],
    lookupInputs: [{ id: productId, match: "featured" }],
    evidenceIds: evidence.map((item) => item.id),
  };
  const variants: readonly CatalogProduct["variants"][number][] = [
    baseVariant,
    {
      ...baseVariant,
      id: `gid://shopify/ProductVariant/fixture-${seed.key}-sand`,
      title: `${seed.title} — Sand`,
      url: `https://example.com/${seed.key}/product?variant=sand`,
      price: { amount: seed.price + 2000, currency },
      checkoutUrl: `https://example.com/${seed.key}/checkout?variant=sand`,
      selectedOptions: [{ name: "Finish", label: "Sand" }],
    },
  ];
  return {
    id: productId,
    title: seed.title,
    description: seed.description,
    rating: { value: 4.1 + ((index % 7) / 10), scaleMin: 1, scaleMax: 5, count: 18 + index * 17 },
    media: [{ type: "image", url: seed.image, altText: seed.title, evidence: "merchant_fact" }],
    options: [{ name: "Finish", values: [{ label: "Standard", available: seed.available !== false, exists: true }, { label: "Sand", available: true, exists: true }] }],
    inferredMetadata: { topFeatures: seed.facts, techSpecs: seed.facts },
    variants,
    priceRanges: [{ currency, min: seed.price, max: seed.price + 2000 }],
    evidence,
    observedAt,
    fixtureDepartment: seed.department,
  };
};

export const FIXTURE_PRODUCTS: readonly CatalogProduct[] = seeds.map(makeProduct);

export function findFixtureProducts(criteria: SearchCriteria): readonly CatalogProduct[] {
  const query = criteria.query.toLocaleLowerCase();
  const tokens = query.split(/\s+/).filter((token) => token.length > 2);
  const category = criteria.filters?.categoryIds?.[0] as DepartmentId | undefined;
  let products = FIXTURE_PRODUCTS.filter((product) => {
    if (category && product.fixtureDepartment !== category) return false;
    const haystack = `${product.title} ${product.description ?? ""} ${product.fixtureDepartment ?? ""}`.toLocaleLowerCase();
    return tokens.length === 0 || tokens.some((token) => haystack.includes(token)) || query === "all";
  });
  if (criteria.filters?.available !== false) products = products.filter((product) => product.variants.some((variant) => variant.available !== false));
  const price = criteria.filters?.price;
  if (price) {
    products = products.filter((product) => product.variants.some((variant) => variant.price.currency === price.currency && (price.min === undefined || variant.price.amount >= price.min) && (price.max === undefined || variant.price.amount <= price.max)));
  }
  return products;
}
